import { MessageBar, MessageBarBody, makeStyles, tokens } from '@fluentui/react-components';
import { QueryAggregate } from '@powerportalspro/core';
import { useT } from '@powerportalspro/react';
import {
  ExportGridButton,
  GridToolbarItems,
  MainGrid,
  NavigateNewRecordGridButton,
  NavigateOpenRecordGridButton,
} from '@powerportalspro/react-fluent';
import { useMemo } from 'react';
import {
  ALL_CONTACTS_VIEW_ID,
  MY_CONTACTS_VIEW_ID,
  getAllContactsView,
  getMyContactsView,
  useCurrentUserId,
} from './AccountAndContactCustomViews';

// The contacts, grouped by their company to begin with (`groupBy`). `allowGrouping` puts a strip
// above the column headers: drag another header onto it — the birthday (a date) or one of the text
// columns — to group each company's contacts by it too, up to three columns. Dropped past the chips
// it is the innermost level, dropped on a chip it goes before it, and a chip dragged elsewhere moves
// its level; a date only goes innermost. Each chip has a button that reverses the order of its
// groups, a span (day, month, quarter or year) for a date column, and an X that takes it away. Only
// the headers of columns the rows can be grouped by can be dragged.
//
// The grid orders its rows by the group first, and puts a header row above each group's first
// record, with the group's name, how many records it holds and its subtotals: the emails counted
// and the average age, under their columns. The totals row has the same figures over every
// contact. Click a group's header to collapse it to that row: its records leave the pages, and the
// pager pages the records of the groups left open. Groups start expanded, and the grid forgets
// which were collapsed when the grouping changes.
//
// Export to Excel writes the same groups, in an outline that collapses them in Excel — the ones
// collapsed in the grid start collapsed — with their subtotals and the totals as formulas.

const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Demos.GridDemos.GridGroupingMainDemo';

const CONTACT_URL = 'demos/contact';

// In each group's header row, under their columns, and in the totals row over every contact: ages
// to one decimal place.
const AGGREGATES = [
  { columnName: 'emailaddress1', aggregates: [{ aggregate: QueryAggregate.Count }] },
  { columnName: 'ppp_age', aggregates: [{ aggregate: QueryAggregate.Average, format: 'N1' }] },
];

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalMNudge,
  },
});

export function GridGroupingMainSample() {
  const styles = useStyles();
  const t = useT();
  const currentUserId = useCurrentUserId();

  const contactViews = useMemo(
    () => [getAllContactsView(), getMyContactsView(currentUserId)],
    [currentUserId],
  );

  return (
    <div className={styles.root}>
      <MessageBar intent="warning" layout="multiline">
        <MessageBarBody>{t(`${STRINGS_BASE}.login-required`)}</MessageBarBody>
      </MessageBar>

      <MainGrid
        tableName="contact"
        minHeight="550px"
        maxHeight="550px"
        groupBy="parentcustomerid"
        allowGrouping
        columnAggregates={AGGREGATES}
        viewIds={[ALL_CONTACTS_VIEW_ID, MY_CONTACTS_VIEW_ID]}
        customViewDefinitions={contactViews}
        defaultViewId={ALL_CONTACTS_VIEW_ID}
      >
        <GridToolbarItems>
          <NavigateNewRecordGridButton url={CONTACT_URL} />
          <NavigateOpenRecordGridButton urlFor={(record) => `${CONTACT_URL}?contactId=${record.id}`} />
          <ExportGridButton />
        </GridToolbarItems>
      </MainGrid>
    </div>
  );
}
